import { useEffect, useState } from 'react';

import { dontUpsell } from '@trello/browser';
import { useMemberNodeId } from '@trello/business-logic-react/member';
import { Entitlements } from '@trello/entitlements';

import { hasAlreadyUsedTrial } from 'app/src/components/FreeTrial/useStartFreeTrialForOrg';
import {
  useEligibleWorkspacesToUpgradeQuery,
  type EligibleWorkspaceToUpgrade,
} from 'app/src/components/SelectWorkspaceToUpgrade';
import { usePlannerWorkspacePremiumFeaturesFragment } from './PlannerWorkspacePremiumFeaturesFragment.generated';

const eligibilityFilter = (workspace: EligibleWorkspaceToUpgrade) =>
  Entitlements.isFree(workspace.offering);

const isEligibleForTrial = (workspace: EligibleWorkspaceToUpgrade) => {
  return !hasAlreadyUsedTrial(workspace.credits ?? []);
};

export type UpsellCohort =
  | 'noUpgradeAvailable'
  | 'paidUser'
  | 'trialAvailable'
  | 'upgradeAvailable'
  | 'upgradeAvailableWithoutUpsell';

/**
 * Hook to determine the upsell cohort and eligibility for a user in the Planner.
 *
 * This hook evaluates the user's workspace status and eligibility for different upsell paths:
 * - Checks if the user has premium Planner features
 * - Determines trial eligibility for free workspaces
 * - Handles cases where upsells should not be shown
 *
 * @returns {Object} An object containing:
 *   - upsellCohort: The determined cohort ('noUpgradeAvailable' | 'paidUser' | 'trialAvailable' | 'upgradeAvailable' | 'upgradeAvailableWithoutUpsell')
 *   - isLoading: Boolean indicating if the eligibility check is in progress
 *
 * @example
 * const { upsellCohort, isLoading } = useUpsellData();
 * if (upsellCohort === 'trialAvailable') {
 *   // Show trial upsell
 * }
 */
export const useUpsellData: () => {
  upsellCohort: UpsellCohort;
  isLoading: boolean;
} = () => {
  const memberNodeId = useMemberNodeId();

  const [upsellCohort, setUpsellCohort] = useState<UpsellCohort>('paidUser');
  const [isLoading, setIsLoading] = useState(true);

  const { data } = usePlannerWorkspacePremiumFeaturesFragment({
    from: { id: memberNodeId },
    optimistic: true,
  });

  const { workspaces, loading: workspacesLoading } =
    useEligibleWorkspacesToUpgradeQuery({
      filter: eligibilityFilter,
    });

  useEffect(() => {
    if (!workspacesLoading && data?.planner?.workspace) {
      const isEligibleForUpsells =
        !data.planner.workspace.premiumFeatures?.includes('advancedPlanner');

      if (isEligibleForUpsells) {
        if (workspaces.some(isEligibleForTrial)) {
          setUpsellCohort('trialAvailable');
        } else if (!workspaces.length || dontUpsell()) {
          // There are some situations where we want to indicate to the user that they don't have the full
          // Planner experience, but we are not allowed to explicitly show them upsells (e.g. desktop).
          setUpsellCohort('upgradeAvailableWithoutUpsell');
        } else {
          setUpsellCohort('upgradeAvailable');
        }
      } else {
        setUpsellCohort('paidUser');
      }

      setIsLoading(false);
    }
  }, [data?.planner?.workspace, workspaces, workspacesLoading]);

  return { upsellCohort, isLoading };
};
